import React from 'react';
import {
  Container,
  Grid,
  Typography,
  Button,
  Alert,
  Skeleton,
  Box,
} from '@mui/material';
import { LineBreaker } from '@/atomicComponent/AtomicComponents';
import LandscapeCard from '@/atomicComponent/LandscapeCard/LandscapeCard';

/**
 * genericListView
 *
 * Props:
 * - title: string
 * - addLabel?: string ('Add')
 * - onAdd?: () => void
 * - filterSlot?: ReactNode (e.g., <ContentFilters .../>)
 * - items?: any[]                // e.g. data?.data
 * - error?: any
 * - isLoading?: boolean
 * - getKey?: (item) => string    // default: item.id || item._id
 * - getImage?: (item) => string  // default: item.image || item.thumbnailLink || item.thumnailLink
 * - renderInfoElement: (item) => ReactNode
 * - renderButtons: (item) => Array<{ title, onClick, backgroundColor }>
 * - gridItem?: { xs?: number, md?: number } // default { xs:12, md:6 }
 * - containerProps?: object
 * - headerRightSlot?: ReactNode   // override Add button with custom header action(s)
 */
const GenericListView = ({
  title,
  addLabel = 'Add',
  onAdd,
  filterSlot,
  items = [],
  error,
  isLoading = false,
  getKey = (item) => item?.id ?? item?._id,
  getImage = (item) =>
    item?.image ?? item?.thumbnailLink ?? item?.thumnailLink ?? null,
  imageStyle,
  renderInfoElement,
  renderButtons,
  gridItem = { xs: 12, md: 6 },
  containerProps = {},
  headerRightSlot,
}) => {
  const showEmpty = !isLoading && !error && (!items || items.length === 0);

  return (
    <Container {...containerProps}>
      {/* Header */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={8}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {headerRightSlot ??
            (onAdd && (
              <Button variant="outlined" onClick={onAdd}>
                {addLabel}
              </Button>
            ))}
        </Grid>
      </Grid>

      <LineBreaker />

      {/* Optional Filters */}
      {filterSlot}

      {/* Error */}
      {error && (
        <>
          <Alert severity="error" sx={{ mb: 2 }}>
            {`Error: ${error?.message || 'Something went wrong.'}`}
          </Alert>
          <LineBreaker />
        </>
      )}

      {/* Loading Skeletons */}
      {isLoading && (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid item {...gridItem} key={`skeleton-${i}`}>
              <Box sx={{ p: 2 }}>
                <Skeleton variant="rectangular" height={180} />
                <Skeleton variant="text" sx={{ mt: 1 }} />
                <Skeleton variant="text" width="60%" />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State */}
      {showEmpty && (
        <Typography variant="body1" color="text.secondary" sx={{ my: 2 }}>
          No items found.
        </Typography>
      )}

      {/* List Items */}
      {!isLoading && !error && items && items.length > 0 && (
        <Grid container spacing={2}>
          {items.map((item) => {
            const key = getKey(item);
            const image = getImage(item);
            const buttons = renderButtons?.(item) || [];
            const infoElement = renderInfoElement?.(item);

            return (
              <Grid item key={key} {...gridItem}>
                <LandscapeCard
                  image={image}
                  imageStyle={imageStyle}
                  buttons={buttons}
                  infoElement={infoElement}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default GenericListView;
